/**
 * ROW 400 (approval 193; William, 19 Sep 2026, R. 81): "I would like to have a new lane built in Care
 * that is just for opt-in queue. Make a new Queue called Opt In (under inbound queue). Then you can
 * have all of the names there in a table. The table shoudl have the following: Name (link to the
 * member's page on the main portal), ID, Cohort, date/time (opt-in), which kit the member would get
 * (based on the master db)."
 *
 * ROW 400 REV2 (his blueprint "Care Adjustments - Opt Ins", 19 Sep 2026; approval 199): "Make a new
 * 01A - New Opt In. All new opt-ins are listed here. Columns: Member ID, Name, Opt In Date/Time,
 * Indication (the actual message) Can be actual (i.e., 1) or implied (anything other then 1, but
 * likely an acceptance, e.g., "yes!!, or a thumbs up icon, etc.), Text. Make the columns sortable by
 * column header. If the member has any issues (DNC, other issues - put a flag). If the member has any
 * other comms with us ... make the row expandable, like we have for the general queue. TAT (time as
 * of now since response given by member). Notes (for the user to make) which will autosave. Top of
 * Page Search (which searches this list only). Action buttons: Left of each row and on top (in col.
 * header) is checkbox. Above columns Green pill button Approve, Yellow pill button Send to Main Dock."
 *
 * The rules of that lane, pure and in ONE place, so the page (pages/OptInQueue.tsx) only renders them:
 * which rows are the lane's (the server's own reading, approval 188: the member's LAST answer per
 * campaign is yes), which of them are open, what each new column says (the Indication as the server
 * read it, the issue flags, the other comms out of the thread model and the archived history, the
 * live TAT), what the search box matches, which rows Select all takes, and the words. Shown only when
 * the deployment says features.replyButtons (MEMBERCARE_REPLY_BUTTONS), the same switch as the
 * reply groups.
 */
import type { CareMarkRow, Channel, EventRow, EventStatus, MemberCommRow, ThreadRow } from '@lib/care-model';
import { CAMPAIGN_META, liveTatMinutes } from '@lib/care-model';
import { hayIncludes } from '@lib/hay';
import { idsForOp, laterMarks, opFits, selectAllTakes } from '@lib/reply-groups';

export const OPT_IN_ROUTE = '/inbound/opt-in';

export type OptInScope = 'open' | 'all';

/** What "still to be worked" means here: the queue's own open buckets (approved rows are cleared). */
export const OPEN_STATUSES: ReadonlySet<EventStatus> = new Set<EventStatus>(['new', 'in_progress', 'in_review']);

/** The statuses the notes route refuses (api/src/lib/event-ops.ts TERMINAL_STATUSES): the Notes cell is read only there. */
export const CLOSED_STATUSES: ReadonlySet<EventStatus> = new Set<EventStatus>(['cleared', 'spam', 'closed', 'closed-no-action']);

export const OPT_IN_WORDING = {
  /** The side menu: his "01A - New Opt In". The number is the nav's `num`, the name its `name`. */
  nav: 'New Opt In',
  navNum: '01A',
  eyebrow: 'Member Care · Inbound queue',
  title: 'New Opt In',
  lede: 'Every new opt-in: each member whose last text answer to a kit offer is yes, one row per member and campaign. Tick rows and Approve to send the kit order to the Portal and Printing, or Send to Main Dock for a person to review in the main queue. Open a row for the whole entry.',
  notEnabled: 'The New Opt In queue is not enabled on this deployment: the reply buttons (MEMBERCARE_REPLY_BUTTONS) are off, so no reply is grouped and there is nothing to list here.',
  loading: 'Loading...',
  scope: { open: 'Open', all: 'All' } as const,
  scopeHelp: {
    open: 'Yeses still in the queue: new, in progress or in review.',
    all: 'Every yes, the approved, referred and archived ones included.',
  } as const,
  /** The Kit cell when the map names no kit for the cohort: the cohort's OWN words, marked. */
  kitPending: (cohort: string): string => `${cohort} (cohort; kit map pending)`,
  noCohort: 'No cohort',
  noKitNamed: 'No kit named',
  mapNotLoaded: (error: string | null): string =>
    `The cohort-to-kit map is not loaded, so the Kit column shows each member's cohort instead of a kit.${error ? ` Reason: ${error}.` : ''}`,
  mapUnnamed: (n: number): string =>
    `${n.toLocaleString()} ${n === 1 ? 'row is' : 'rows are'} in a cohort the kit map does not name; the Kit column shows the cohort's words for ${n === 1 ? 'it' : 'them'}.`,
  mapLoaded: (entries: number): string => `Kit map loaded: ${entries.toLocaleString()} cohort${entries === 1 ? '' : 's'} named.`,
  empty: (client: string, scope: OptInScope): string =>
    scope === 'open' ? `No open opt-in replies for ${client}.` : `No opt-in replies for ${client}.`,
  emptySearch: (q: string): string => `No opt-in replies match "${q}" in this list.`,
  blocked: 'Opt In refuses this entry',
  unmatched: 'unmatched',
  noun: 'members',
  search: {
    label: 'Search this list',
    placeholder: 'Search this list: name, member ID, text, cohort, kit, campaign, phone',
    clear: 'Clear',
  } as const,
  indication: { actual: 'Actual', implied: 'Implied', '': '' } as const,
  indicationHelp: {
    actual: 'The member texted exactly 1.',
    implied: 'Any other yes: "yes!!", "one", "ok", or a sentence the platform read as a yes. Read the text.',
  } as const,
  issues: {
    none: '-',
    optedOut: 'Opted out (record)',
    optedOutHelp: 'The permanent opt-out record has this member suppressed. Open the Opt-outs screen to review it.',
    doNotSolicit: 'Do not solicit (plan)',
    doNotSolicitHelp: "The plan's own roster marks this member do not solicit.",
  } as const,
  comms: {
    none: '-',
    count: (open: number, archived: number): string => {
      const parts = [open ? `${open} open` : '', archived ? `${archived} archived` : ''].filter(Boolean);
      return parts.join(', ');
    },
    expand: 'Show the other comms',
    collapse: 'Hide the other comms',
    openHead: 'Open in the queue',
    archivedHead: (builtAt: string): string => `Archived history (member_care.db, built ${builtAt}; up to ten minutes behind)`,
    archivedUnread: 'Archived history not read: member_care.db could not be read, so only the open comms are shown.',
    archivedLoading: 'Reading the archived history...',
  } as const,
  notes: {
    placeholder: 'Notes (autosaved)',
    loadFirst: 'Notes on file; click to load them',
    loading: 'Loading notes...',
    saved: 'Saved',
    unsaved: 'Unsaved',
    saving: 'Saving...',
    failed: (why: string): string => `Not saved: ${why}`,
    readOnly: 'Closed entry: notes are read only here',
  } as const,
  press: {
    approve: 'Approve',
    approveHelp: 'Approve the kit for the ticked members: the approved line goes to the kit-requests record, the order envelope to the Portal for Printing, and each entry moves to your Archive as "Approved by <your initials>".',
    refer: 'Send to Main Dock',
    referHelp: 'Send the ticked members to the main Inbound queue for a person to review: an under-review line goes to the kit-requests record, a new entry appears in the main queue naming who sent it, and each entry moves to your Archive.',
    referReasonPrompt: 'Send to Main Dock: a reason for the reviewer (optional, one for the whole press). Leave it empty for none.',
    selectAll: (n: number): string => `Select all on screen (${n})`,
    clear: 'Clear selection',
    readFirst: (n: number): string => `${n} to read first or blocked, tick one by one`,
    confirmApprove: (n: number): string => `Approve: ${n} member${n === 1 ? '' : 's'}?`,
    confirmRefer: (n: number): string => `Send to Main Dock: ${n} member${n === 1 ? '' : 's'}?`,
    /** Row 496: a press of more than 50 goes in parts; the first part that fails ends the run. */
    stoppedAfter: (n: number, total: number): string => `stopped after part ${n} of ${total}; the other ${total - n} part${total - n === 1 ? ' was' : 's were'} not sent. Press again for them once the cause is fixed`,
    /**
     * ROW 521 (William, 22 Sep 2026). The operator's own initials, typed once and remembered in this
     * browser. The signed-in account is recorded underneath on every press either way; this only says
     * WHO PRESSED, which the account cannot say when one person presses at another's machine.
     */
    initialsLabel: 'Your initials',
    initialsPlaceholder: 'MH',
    initialsHelp: 'Stamped on every entry this press closes, in place of the initials taken from the signed-in account. The account is still recorded underneath. Letters only, up to four. Leave it empty and the account supplies its own.',
    initialsBad: 'Letters only, up to four.',
    initialsAccount: (initials: string, email: string): string =>
      `Empty: presses are stamped ${initials}, from the signed-in account ${email}.`,
    initialsTyped: (initials: string, email: string): string =>
      `Presses are stamped ${initials}. The signed-in account ${email} is recorded underneath.`,
  } as const,
  columnsTip: {
    select: 'Tick the rows to act on. The header box ticks every row on screen that Select all may take.',
    id: 'The member ID.',
    name: "The member's name as the roster has it. A link to the member's page on the main portal when the deployment names one.",
    optin: 'When the yes arrived, on the Pacific clock.',
    indication: 'Actual: the member texted exactly 1. Implied: any other yes (read the text).',
    text: "The member's words, as the reply arrived.",
    issues: 'What stands against this member: a later STOP or 2, the do-not-text list, the permanent opt-out record, the plan\'s do-not-solicit mark.',
    comms: 'Other communications from this member: open ones in the queue, and the archived history. Click to expand.',
    tat: 'Time since the member answered, ticking; frozen once the entry is sent or approved.',
    notes: 'Your notes on the entry, saved as you type.',
    cohort: "The member's cohort, in the campaign roster's own words. The cohort decides the kit.",
    kit: 'The kit the cohort maps to in the published cohort-to-kit map. A cohort the map does not name shows its own words, marked pending; no kit is ever guessed.',
    campaign: 'The campaign the reply answered.',
    offer: 'The offer the text reply answered. The cohort, not the offer, decides the kit.',
    status: "Where the entry is in the queue. An approved or referred yes is cleared and shows under All.",
  },
} as const;

/** A row of THIS lane: the server put it in the Opt In group (approval 188). Never the class alone. */
export function isOptInRow(r: Pick<EventRow, 'replyGroupHint'>): boolean {
  return r.replyGroupHint === 'optin';
}

export function inScope(r: Pick<EventRow, 'status'>, scope: OptInScope): boolean {
  return scope === 'all' || OPEN_STATUSES.has(r.status);
}

/** The lane's rows out of the queue's, in the queue's own order. */
export function optInRows<T extends Pick<EventRow, 'replyGroupHint' | 'status'>>(items: readonly T[], scope: OptInScope): T[] {
  return items.filter((r) => isOptInRow(r) && inScope(r, scope));
}

/** A row the Opt In press would refuse (approval 187): greyed, with the reason on it. */
export function isBlockedRow(r: Pick<EventRow, 'laterRefusal'>): boolean {
  return !!r.laterRefusal;
}

/** Greyed like an opted-out row on the queue: a colour, backed by the words in the Issues column. */
export const BLOCKED_ROW_STYLE: { background: string; opacity: number } = { background: 'var(--paper-2)', opacity: 0.72 };

export interface KitCell {
  /** What the cell reads. */
  text: string;
  /** True when the map named no kit and the cell is showing the cohort's words instead. */
  pending: boolean;
  /** True when the row has neither a kit nor a cohort. */
  none: boolean;
}

/** The Kit cell: the kit, else the cohort's own words marked pending, else nothing. Never a guess. */
export function kitCell(r: Pick<EventRow, 'replyKit' | 'replyCohort'>): KitCell {
  const kit = (r.replyKit ?? '').trim();
  if (kit) return { text: kit, pending: false, none: false };
  const cohort = (r.replyCohort ?? '').trim();
  if (cohort) return { text: OPT_IN_WORDING.kitPending(cohort), pending: true, none: false };
  return { text: '', pending: false, none: true };
}

/** The Kit slicer's value: the kit when named, else blank (offered as "No kit named"). */
export function kitSliceValue(r: Pick<EventRow, 'replyKit'>): string {
  return (r.replyKit ?? '').trim();
}

/** How many rows on screen carry a cohort the map names no kit for. */
export function unnamedCohortCount(rows: ReadonlyArray<Pick<EventRow, 'replyKit' | 'replyCohort'>>): number {
  return rows.filter((r) => kitCell(r).pending).length;
}

/** A time as the queue shows one ("Sep 18, 1:11 PM"), on the Pacific clock whatever the browser's zone. */
export function fmtTsPt(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  }).format(d);
}

/** The marks a row carries, in the words the Inbound queue uses (one table, lib/reply-groups.ts). */
export function optInFlags(r: Pick<EventRow, 'laterRefusal' | 'answerChanged' | 'replyNote' | 'replyReadFirst'>): ReturnType<typeof laterMarks> {
  return laterMarks(r);
}

/** The Flags column's sort and search text: the marks' words, or '' for a row carrying none. */
export function flagsText(r: Pick<EventRow, 'laterRefusal' | 'answerChanged' | 'replyNote' | 'replyReadFirst'>): string {
  return optInFlags(r).map((m) => m.text).join('; ');
}

/** The scope chips' counts over the lane's rows. */
export function scopeCounts(rows: ReadonlyArray<Pick<EventRow, 'replyGroupHint' | 'status'>>): Record<OptInScope, number> {
  const lane = rows.filter(isOptInRow);
  return { open: lane.filter((r) => inScope(r, 'open')).length, all: lane.length };
}

/* ------------------------------------------ rev2: the new columns ------------------------------------------ */

/** His Indication, as the server read it: "Actual", "Implied", or '' for a row that is not a yes. */
export function indicationLabel(r: Pick<EventRow, 'replyIndication'>): string {
  return OPT_IN_WORDING.indication[r.replyIndication ?? ''] ?? '';
}

export interface IssueMark {
  key: string;
  text: string;
  pill: string;
  title: string;
}

/**
 * THE ISSUES FLAG. What the list row already carries about the member, and only that: the 187 later
 * refusal (a later STOP or 2, the do-not-text list; blocks Approve), the 188 Read first marker (the
 * header box skips the row, so the row must say why), the permanent opt-out record's suppression
 * (`optedOut`) and the plan's own do-not-solicit mark (`doNotSolicit`). The DNC master table (the
 * platform's, read per request on tab 12) and the USPS rejections (the address decisions record) are
 * NOT reachable from the list projection without a new read per poll, so they are not here; the
 * Exceptions tabs remain where those are worked.
 */
export function issueMarks(r: Pick<EventRow, 'laterRefusal' | 'answerChanged' | 'replyNote' | 'replyReadFirst' | 'optedOut' | 'doNotSolicit'>): IssueMark[] {
  const out: IssueMark[] = laterMarks(r).filter((m) => m.key === 'later-refusal' || m.key === 'read-first').map((m) => ({ ...m }));
  if (r.optedOut) out.push({ key: 'opted-out', text: OPT_IN_WORDING.issues.optedOut, pill: 'pill-accent', title: OPT_IN_WORDING.issues.optedOutHelp });
  if (r.doNotSolicit) out.push({ key: 'do-not-solicit', text: OPT_IN_WORDING.issues.doNotSolicit, pill: 'pill-danger', title: OPT_IN_WORDING.issues.doNotSolicitHelp });
  return out;
}

/** The Issues column's sort and search text. */
export function issuesText(r: Parameters<typeof issueMarks>[0]): string {
  return issueMarks(r).map((m) => m.text).join('; ');
}

/** A member id compared with its leading zeros trimmed, the thread model's own key. */
export function memberKey(v: string | null | undefined): string {
  return String(v ?? '').trim().replace(/^0+/, '');
}

/** The queue's member threads, by trimmed member id (the thread model keys a matched row `member:<id>`). */
export function threadsByMember(threads: readonly ThreadRow[] | undefined): Map<string, ThreadRow> {
  const out = new Map<string, ThreadRow>();
  for (const t of threads ?? []) {
    if (t.kind !== 'member') continue;
    const k = memberKey(t.memberId);
    if (k && !out.has(k)) out.set(k, t);
  }
  return out;
}

/** The archived rows by trimmed member id, newest first as the server sends them. */
export function archivedByMember(rows: readonly MemberCommRow[] | undefined): Map<string, MemberCommRow[]> {
  const out = new Map<string, MemberCommRow[]>();
  for (const r of rows ?? []) {
    const k = memberKey(r.memberId);
    if (!k) continue;
    const list = out.get(k) ?? [];
    list.push(r);
    out.set(k, list);
  }
  return out;
}

export interface OtherComms {
  /** The member's OTHER open entries in the queue, newest first, from the thread model. */
  open: EventRow[];
  /** The member's archived entries, newest first, from member_care.db; never one already listed as open. */
  archived: MemberCommRow[];
}

/**
 * The other comms of one row: its member's thread minus itself (the general queue's own grouping,
 * reused), plus the archived history minus anything the live listing already shows. An unmatched row
 * has no member and therefore no other comms here.
 */
export function otherCommsOf(
  row: Pick<EventRow, 'eventId' | 'memberId'>,
  threads: ReadonlyMap<string, ThreadRow>,
  eventsById: ReadonlyMap<string, EventRow>,
  archived: ReadonlyMap<string, MemberCommRow[]>,
): OtherComms {
  const k = memberKey(row.memberId);
  if (!k) return { open: [], archived: [] };
  const open: EventRow[] = [];
  for (const id of threads.get(k)?.eventIds ?? []) {
    if (id === row.eventId) continue;
    const ev = eventsById.get(id);
    if (ev) open.push(ev);
  }
  const seen = new Set([row.eventId, ...open.map((e) => e.eventId)]);
  const arch = (archived.get(k) ?? []).filter((r) => !seen.has(r.eventId));
  return { open, archived: arch };
}

/** The distinct trimmed member ids of the rows, in row order: what the archived read asks for. */
export function memberIdsOf(rows: ReadonlyArray<Pick<EventRow, 'memberId'>>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const r of rows) {
    const k = memberKey(r.memberId);
    if (k && !seen.has(k)) {
      seen.add(k);
      out.push(k);
    }
  }
  return out;
}

/** His TAT: minutes since the member answered, ticking; the server's frozen figure once sent or approved. */
export function laneTat(r: Pick<EventRow, 'tatMinutes' | 'receivedTs' | 'sentToReviewTs'>, nowMs: number): number | null {
  return r.tatMinutes ?? liveTatMinutes(r.receivedTs, r.sentToReviewTs || undefined, nowMs);
}

/**
 * THE TOP-OF-PAGE SEARCH, over this list only: token-AND (every word must land somewhere) over the
 * name, the member id (padded or not), the text, the cohort, the kit, the campaign's label and slug,
 * the phone, the indication, the issues and the status. Never a server round trip: the queue's own
 * `q` would search the whole queue and rebuild the thread view.
 */
export function matchesOptInQuery(r: EventRow, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const hay = [
    r.memberName, r.memberId, r.preview, r.subject, r.replyCohort ?? '', r.replyKit ?? '',
    r.campaign ?? '', r.campaign ? CAMPAIGN_META[r.campaign]?.label ?? '' : '', r.fromPhone, r.fromAddress,
    indicationLabel(r), issuesText(r), r.status.replace(/_/g, ' '),
  ].join(' ').toLowerCase();
  return hayIncludes(hay, needle);
}

/**
 * Which rows the HEADER tick box takes (the 188 rule stands: never a Read first row, and never a row
 * blocked by a later refusal, which Approve would refuse). Both stay tickable one by one, because
 * Send to Main Dock takes a blocked row.
 */
export function bulkTakes(r: Pick<EventRow, 'replyReadFirst' | 'laterRefusal'>): boolean {
  return selectAllTakes(r) && !isBlockedRow(r);
}

/** The ids the GREEN pill sends: the ticked rows on screen that Approve fits (a blocked row never rides). */
export function approveIds(visible: ReadonlyArray<EventRow>, checked: ReadonlySet<string>): string[] {
  return idsForOp('opt-in', visible, checked);
}

/** The ids the YELLOW pill sends: the ticked rows on screen that a referral fits (a blocked row does ride). */
export function referIds(visible: ReadonlyArray<EventRow>, checked: ReadonlySet<string>): string[] {
  return idsForOp('refer', visible, checked);
}

/** Whether a row can take either pill at all (the tick box is offered for these). */
export function tickable(r: EventRow): boolean {
  return opFits('opt-in', r) || opFits('refer', r);
}

/** How the notes cell reads its save state, in one table. */
export type NotesState = 'idle' | 'dirty' | 'saving' | 'saved' | 'failed';

export function notesMark(state: NotesState, why = ''): string {
  switch (state) {
    case 'dirty': return OPT_IN_WORDING.notes.unsaved;
    case 'saving': return OPT_IN_WORDING.notes.saving;
    case 'saved': return OPT_IN_WORDING.notes.saved;
    case 'failed': return OPT_IN_WORDING.notes.failed(why);
    default: return '';
  }
}

/** The debounce a keystroke waits before the note is saved: long enough to finish a sentence, short enough to feel automatic. */
export const NOTES_SAVE_DELAY_MS = 800;

/* ------------------------------------------ row 419: the three lanes ------------------------------------------ */

/**
 * ROW 419 (William, 19 Sep 2026, his OneNote page "Care Connect: Opt Outs/STOP"): "I would like a
 * parallel systems (new sub-tab) for the Opt-Out (decline offer), and for the STOP (DNC). Make those
 * lists in the same way, and remove from the primary input queue. And, ensure all logic for inclusion
 * in the master DB, and the DNC db and in the Care db are all there. And, once set, remove them from
 * the main queue, put in care's archive." His three conditions: OPT IN = continue to interact. OPT OUT
 * = stop communications for this campaign only. STOP = every communication, forever. Approval 211.
 * His R. 87 (10:27 PM the same night): "backfill. Make sure the 2's (opt-out) and the STOPs are in the
 * DNC db and all other places and also for approval from care": the block is ALREADY IN FORCE from the
 * member's reply (the platform lists a STOP on arrival and, from its v80, marks a 2 as it arrives);
 * Care's press is its APPROVAL, recorded on the member's file and the archive, and never delays it.
 *
 * THE THREE LANES of the 01A page, one tab strip, one grid: Opt In (today's, unchanged), Opt Out (the
 * member's LAST answer per campaign is a 2: the server's own Opt Out group, the same reading Opt In
 * uses, never a second rule), STOP (the platform's class STOP, no START having lifted it). The GREEN
 * press on each new tab is the EXISTING op (opt-out, stop); the YELLOW Send to Main Dock stays on
 * every tab wired to the existing refer op. A row that already carries its mark (its own action, or a
 * Care mark on the member from another entry, read from member_care.db) shows the mark and its time
 * instead of a tick box and is never taken by Select all.
 */
export type LaneKind = 'optin' | 'optout' | 'stop';
export const LANE_KINDS: readonly LaneKind[] = ['optin', 'optout', 'stop'];

/** The query key that keeps the tab across a reload: `/inbound/opt-in?lane=optout`. Absent = Opt In. */
export const LANE_QUERY_KEY = 'lane';

/**
 * MIRROR of api/src/lib/mark-kind.ts MARK_STOP_KIND, the ONE spelling of the stop kind on a mark
 * (pinned in lockstep by tests/unit/optout-stop-tabs.test.tsx). 'optout' is the other kind.
 */
export const MARK_STOP_KIND = 'stop';
export const MARK_KIND_OPTOUT = 'optout';

export function laneFromSearch(search: string): LaneKind {
  const v = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get(LANE_QUERY_KEY);
  return v === 'optout' || v === 'stop' ? v : 'optin';
}

/** The search string with the lane set (or cleared for Opt In, today's URL). */
export function searchWithLane(search: string, lane: LaneKind): string {
  const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  if (lane === 'optin') q.delete(LANE_QUERY_KEY); else q.set(LANE_QUERY_KEY, lane);
  const s = q.toString();
  return s ? `?${s}` : '';
}

/**
 * ROW 485 (William, 20 Sep 2026, verbatim): "It should be three left side navigation - 01A New Opt In;
 * 01B New Opt Out; and 01C New STOP. If you can move them there." One menu line per lane, each line's
 * id the lane's own address (the route, plus the lane query for every lane but Opt In), so the two
 * direct links of 20 Sep keep working and the menu highlight follows the lane on screen. NAVIGATION
 * ONLY: no press, route, record, count or page wording changes with it. The shell's literal lines are
 * pinned in lockstep with this table (tests/unit/lane-nav.test.tsx), so the day a fourth lane joins
 * LANE_KINDS (row 458's "Asked to come back", 01D) the test fails until its line exists.
 */
export const LANE_NAV: Readonly<Record<LaneKind, { num: string; name: string; meta: string }>> = {
  optin: { num: '01A', name: 'New Opt In', meta: 'Yeses' },
  optout: { num: '01B', name: 'New Opt Out', meta: 'Noes' },
  stop: { num: '01C', name: 'New STOP', meta: 'Stops' },
};

/** The menu id of a lane, which is also its address: `/inbound/opt-in`, `/inbound/opt-in?lane=optout`, ... */
export function laneNavId(lane: LaneKind): string {
  return `${OPT_IN_ROUTE}${searchWithLane('', lane)}`;
}

export interface LaneDef {
  /** The tab's label. */
  tab: string;
  /** The page title and lede on this tab (Opt In keeps its own). */
  title: string;
  lede: string;
  /** The date column's heading. */
  dateLabel: string;
  /** The GREEN press: the existing op it sends and its label. */
  greenOp: 'opt-in' | 'opt-out' | 'stop';
  green: string;
  greenHelp: string;
  confirmGreen: (n: number) => string;
  /** The mark kind this lane records ('' on Opt In, which records none). */
  markKind: '' | typeof MARK_KIND_OPTOUT | typeof MARK_STOP_KIND;
  /** The grid's remembered-layout key (Opt In keeps today's). */
  viewKey: string;
  /** The words of the mark on a row and in the Marked view. */
  markWord: string;
  /** The one-line note in the operator's words; the scope words differ on Opt Out (see scopeNote). */
  note: (scope: string) => string;
  empty: (client: string, scope: OptInScope) => string;
  emptySearch: (q: string) => string;
  markedEmpty: (client: string) => string;
}

export const LANES: Readonly<Record<LaneKind, LaneDef>> = {
  optin: {
    tab: 'Opt In', title: OPT_IN_WORDING.title, lede: OPT_IN_WORDING.lede, dateLabel: 'Opt In date/time',
    greenOp: 'opt-in', green: OPT_IN_WORDING.press.approve, greenHelp: OPT_IN_WORDING.press.approveHelp, confirmGreen: OPT_IN_WORDING.press.confirmApprove,
    markKind: '', viewKey: 'opt-in-queue', markWord: '',
    note: () => '',
    empty: OPT_IN_WORDING.empty, emptySearch: OPT_IN_WORDING.emptySearch, markedEmpty: () => '',
  },
  optout: {
    tab: 'Opt Out', title: 'Opt Out', dateLabel: 'Opt Out date/time',
    lede: 'Every new opt-out: each member whose last text answer to a kit offer is 2 (no), one row per member and campaign. Tick rows and Approve opt out to record it on the member\'s file and the DNC list and move the entry to the archive. Open a row for the whole entry. A voicemail or an email asking out is listed here too: open it and record the opt out on the case.',
    greenOp: 'opt-out', green: 'Approve opt out (this campaign)',
    greenHelp: 'Approve the opt-out for the ticked members: the campaign-only opt-out goes on the member\'s file (the master DB) and the DNC list, and each entry moves to your Archive as resolved. The block is already in force from the member\'s reply; this records Care\'s approval.',
    confirmGreen: (n: number): string => `Approve opt out (this campaign): ${n} member${n === 1 ? '' : 's'}?`,
    markKind: MARK_KIND_OPTOUT, viewKey: 'opt-out-queue', markWord: 'Opted out',
    note: (scope: string): string =>
      `Stops texts for ${scope}; the member stays contactable for other campaigns. The block is already in force from the member's reply (the texting platform marks a 2 as it arrives); Care's approval records it on the member's file and the DNC list and never delays it; the entry moves to the archive.`,
    empty: (client: string, scope: OptInScope): string => (scope === 'open' ? `No open opt-out replies for ${client}.` : `No opt-out replies for ${client}.`),
    emptySearch: (q: string): string => `No opt-out replies match "${q}" in this list.`,
    markedEmpty: (client: string): string => `No archived opt-out marks for ${client}.`,
  },
  stop: {
    tab: 'STOP', title: 'STOP', dateLabel: 'STOP date/time',
    lede: 'Every STOP: each member whose text reply is a STOP the platform listed, one row per entry. Tick rows and Approve STOP to record do not contact on the member\'s file, the DNC list and Care\'s archive. Open a row for the whole entry.',
    greenOp: 'stop', green: 'Approve STOP (all channels)',
    greenHelp: 'Approve the STOP for the ticked members: do not contact goes on the member\'s file (the master DB) and the DNC list as a locked mark, and each entry moves to your Archive as resolved. The texting platform listed the number when the STOP arrived; this records Care\'s approval.',
    confirmGreen: (n: number): string => `Approve STOP (all channels): ${n} member${n === 1 ? '' : 's'}?`,
    markKind: MARK_STOP_KIND, viewKey: 'stop-queue', markWord: 'STOP',
    note: (): string =>
      'Stops every communication forever, all channels. The block is already in force from the member\'s reply (the texting platform listed the number when the STOP arrived); Care\'s approval writes it to the member\'s file, the DNC list and Care\'s archive and never delays it.',
    empty: (client: string, scope: OptInScope): string => (scope === 'open' ? `No open STOP replies for ${client}.` : `No STOP replies for ${client}.`),
    emptySearch: (q: string): string => `No STOP replies match "${q}" in this list.`,
    markedEmpty: (client: string): string => `No archived STOP marks for ${client}.`,
  },
};

export const LANE_WORDING = {
  tablist: 'Opt In, Opt Out and STOP',
  marked: 'Marked',
  markedHelp: 'The archived entries carrying this mark: Care\'s presses (from this tab, the drawer or the Exceptions screens) and the platform\'s own reading of the reply. Read only.',
  markedOff: 'Back to the work queue',
  markedHead: (builtAt: string): string => `Archived marks, from member_care.db (built ${builtAt}; up to ten minutes behind). Read only: nothing here lifts a mark; a lift is recorded on the Exceptions review screens.`,
  markedUnread: 'The archived marks could not be read: member_care.db is not readable on this deployment.',
  markedTableAbsent: 'member_care.db was built before the care_marks table; the archived marks will show after the next build with the new builder.',
  markedLoading: 'Reading the archived marks...',
  /** Send to Main Dock on the two new tabs: the refer press writes an under-review KIT line and takes a yes only. */
  referNotHere: 'Send to Main Dock takes a yes only (the refer press writes an under-review kit line for the Portal), so it takes no row on this tab; open a row to review it.',
  sourceCare: 'Care press',
  sourcePlatform: 'the platform (the reply)',
  inForce: 'In force from the reply',
  inForceHelp: {
    optout: 'The texting platform marks a 2 as it arrives (from its v80; the 19 Sep backfill covers the earlier ones). Care\'s approval records it and never delays it.',
    stop: 'The texting platform listed the number when the STOP arrived. Care\'s approval records it and never delays it.',
  } as const,
  scopeCampaignOnly: 'this campaign only',
  scopeProgramYear: (program: string, year: number): string => `this program year (${program} ${year}) only`,
  scopeMixed: 'this campaign only, or its program year where the registry names one',
  markedColumns: {
    id: 'Member ID', name: 'Name', when: 'Marked', by: 'By', source: 'Source', campaign: 'Campaign', scope: 'Program year', text: 'Text', status: 'Status', entry: 'Entry',
  } as const,
  /** C0876ADB point 6: a voicemail or an email asking out, in the tick cell instead of a box. */
  nonText: (channel: Channel): string => (channel === 'Voice' ? 'Voicemail' : 'Email'),
  nonTextHelp: (channel: Channel): string =>
    `${channel === 'Voice' ? 'A voicemail' : 'An email'} asking out: the presses on this tab take text replies only, so open the row and record the opt out on the case.`,
} as const;

/**
 * C0876ADB POINT 6 (the office, 15 Oct 2026 11:42 AM, on William's 11:40 AM word; re-pointed to Care at
 * 11:55 AM): "sort a voice or email entry whose suggestion or request is opt-out into 01B New Opt Out".
 * A voicemail or an email carries no text reply, so the server's reading never puts it in a reply group,
 * and until this build the six WY Blue voicemails asking out (13 to 15 Oct) sat in the main Inbound queue
 * only. The signals are the two the sorted folders already read (lib/sort-folders.ts), never a new reading
 * of the member's words: the console's suggestion (Claire's captured intent is high, a text rule low) and
 * an ask recorded in the case's Requests. A text reply is never this rule's: its lane is the server's
 * reading. The presses take text replies only (OP_CLASSES), so such a row is never ticked; it is opened
 * and the opt out recorded on the case.
 */
export const NON_TEXT_OPTOUT_CHANNELS: readonly Channel[] = ['Voice', 'Email'];

/** The row fields every lane rule reads. */
export type LaneRow = Pick<EventRow, 'replyGroupHint' | 'replyClass' | 'resubscribedAfterStop' | 'channel' | 'suggestedAction' | 'requestTypes'>;

/** A voicemail or an email whose suggestion or recorded request is opt-out: an Opt Out row with no press. */
export function isNonTextOptOut(r: Pick<EventRow, 'replyClass' | 'channel' | 'suggestedAction' | 'requestTypes'>): boolean {
  if (r.replyClass) return false;
  if (!NON_TEXT_OPTOUT_CHANNELS.includes(r.channel)) return false;
  return r.suggestedAction === 'opt-out' || (r.requestTypes ?? []).includes('opt-out');
}

/** A row of a lane, by the server's own reading: never a second rule on the screen. */
export function isLaneRow(r: LaneRow, lane: LaneKind): boolean {
  if (lane === 'optin') return isOptInRow(r);
  // The server's own Opt Out group (the member's LAST answer on the campaign is no). A class-STOP reply
  // is the STOP tab's whatever group a reading gave it: the opt-out press never acts on a STOP
  // (OP_CLASSES, mirrored from the server), so the lane never offers one. A voicemail or an email asking
  // out joins it (C0876ADB point 6, above).
  if (lane === 'optout') return (r.replyGroupHint === 'optout' && r.replyClass !== 'STOP') || isNonTextOptOut(r);
  // A STOP is a STOP whatever came after it, except a START (the platform's own resubscribe words),
  // which the queue already reads as `resubscribedAfterStop`; a later 1 never lifts a STOP (187 a).
  return r.replyClass === 'STOP' && !r.resubscribedAfterStop;
}

/** The lane's rows out of the queue's, in the queue's own order. On Opt In, byte for byte optInRows. */
export function laneRows<T extends LaneRow & Pick<EventRow, 'status'>>(items: readonly T[], lane: LaneKind, scope: OptInScope): T[] {
  if (lane === 'optin') return optInRows(items, scope);
  return items.filter((r) => isLaneRow(r, lane) && inScope(r, scope));
}

export function laneCounts(rows: ReadonlyArray<LaneRow & Pick<EventRow, 'status'>>, lane: LaneKind): Record<OptInScope, number> {
  if (lane === 'optin') return scopeCounts(rows);
  const mine = rows.filter((r) => isLaneRow(r, lane));
  return { open: mine.filter((r) => inScope(r, 'open')).length, all: mine.length };
}

/** The mark a row already carries, for the tab: on the row or on the member (Care's, from another entry). */
export interface RowMark {
  kind: string;
  at: string;
  by: string;
  byName: string;
  campaign: string | null;
  /** 'entry': the row's own action; 'member': a Care mark of the member on another entry, from member_care.db. */
  via: 'entry' | 'member';
}

/** The members' marks by trimmed member id, Care's and the platform's alike, as the read answered them. */
export function marksByMember(marks: readonly CareMarkRow[] | undefined): Map<string, CareMarkRow[]> {
  const out = new Map<string, CareMarkRow[]>();
  for (const m of marks ?? []) {
    const k = memberKey(m.memberId);
    if (!k) continue;
    const list = out.get(k) ?? [];
    list.push(m);
    out.set(k, list);
  }
  return out;
}

/** Whether a mark of `kind` answers this lane: a stop answers both lanes (a stop is every campaign); an opt-out answers Opt Out on the same campaign. */
function markAnswers(lane: LaneKind, kind: string, markCampaign: string | null, rowCampaign: string | null): boolean {
  if (kind === MARK_STOP_KIND) return true;
  return lane === 'optout' && kind === MARK_KIND_OPTOUT && (!markCampaign || !rowCampaign || markCampaign === rowCampaign);
}

/**
 * THE MARK ON A ROW, or null. Never on Opt In (its grid is today's, byte for byte). On the two tabs:
 * the row's own action first (the server's replyMark), else a CARE mark of the member from another
 * entry (member_care.db, up to ten minutes behind). The platform's own reading (source 'platform') is
 * NOT a mark here: that is the block in force from the reply, which Care's press exists to approve.
 */
export function rowMark(
  r: Pick<EventRow, 'memberId' | 'campaign' | 'replyMark'>,
  lane: LaneKind,
  marks: ReadonlyMap<string, CareMarkRow[]>,
): RowMark | null {
  if (lane === 'optin') return null;
  const own = r.replyMark;
  if (own && markAnswers(lane, own.kind, own.campaign, r.campaign)) {
    return { kind: own.kind, at: own.at, by: own.by, byName: own.byName, campaign: own.campaign, via: 'entry' };
  }
  const k = memberKey(r.memberId);
  if (!k) return null;
  for (const m of marks.get(k) ?? []) {
    if (m.source !== 'care') continue;
    if (markAnswers(lane, m.kind, m.campaign, r.campaign)) return { kind: m.kind, at: m.markedAt, by: m.markedBy, byName: m.markedByName, campaign: m.campaign, via: 'member' };
  }
  return null;
}

/** Whether the block is already in force from the reply itself (his R. 87), read off the platform's class. */
export function inForceFromReply(r: Pick<EventRow, 'replyClass'>, lane: LaneKind): boolean {
  if (lane === 'optout') return r.replyClass === 'DECLINE_CAMPAIGN';
  if (lane === 'stop') return r.replyClass === 'STOP';
  return false;
}

/** The green op a lane's rows fit, on the server's own table (opFits mirrors it). */
export function laneTickable(r: EventRow, lane: LaneKind, mark: RowMark | null): boolean {
  if (lane === 'optin') return tickable(r);
  if (mark) return false;
  return opFits(LANES[lane].greenOp, r) || opFits('refer', r);
}

/** Whether Select all takes the row: on Opt In today's rule; on the two tabs never a marked row and never a Read first row. */
export function laneBulkTakes(r: EventRow, lane: LaneKind, mark: RowMark | null): boolean {
  if (lane === 'optin') return bulkTakes(r);
  return !mark && selectAllTakes(r);
}

/** The ids the GREEN pill sends on a lane: the ticked rows on screen the op fits, never a marked row. */
export function laneGreenIds(visible: ReadonlyArray<EventRow>, checked: ReadonlySet<string>, lane: LaneKind, markOf: (r: EventRow) => RowMark | null): string[] {
  if (lane === 'optin') return approveIds(visible, checked);
  return idsForOp(LANES[lane].greenOp, visible.filter((r) => !markOf(r)), checked);
}

/** The ids the YELLOW pill sends on a lane (the refer op), never a marked row. */
export function laneReferIds(visible: ReadonlyArray<EventRow>, checked: ReadonlySet<string>, lane: LaneKind, markOf: (r: EventRow) => RowMark | null): string[] {
  if (lane === 'optin') return referIds(visible, checked);
  return idsForOp('refer', visible.filter((r) => !markOf(r)), checked);
}

/** The operator's initials by the row 400 rev2 rule (marny.hudson -> MH), for "by MH". */
/**
 * ROW 521: the operator's typed initials, kept in THIS browser only (localStorage), so Marny types
 * them once and every later press carries them. Per viewer and per browser by design: it is not an
 * identity and it is not a permission, it is the two letters that go on the entry beside the account.
 *
 * Every read and write is wrapped, because storage throws in a private window and can come back
 * empty, and the press must work either way: empty means the account's own initials, which is what
 * every press did before this build.
 */
export const TYPED_INITIALS_KEY = 'membercare.press.initials';
export const MAX_TYPED_INITIALS = 4;
const TYPED_INITIALS_RE = /^[A-Za-z]{1,4}$/;

/** True when the value is one the press route will accept. An EMPTY value is valid and means "use the account's". */
export function typedInitialsOk(v: string): boolean {
  const t = v.trim();
  return t === '' || TYPED_INITIALS_RE.test(t);
}

/** The stored value, upper-cased, or '' when there is none or storage refuses. Never throws. */
export function readTypedInitials(): string {
  try {
    const v = String(window.localStorage.getItem(TYPED_INITIALS_KEY) ?? '').trim().toUpperCase();
    return TYPED_INITIALS_RE.test(v) ? v : '';
  } catch {
    return '';
  }
}

/** Remembers a valid value, forgets an empty one, and does nothing at all when storage refuses. */
export function writeTypedInitials(v: string): void {
  const t = v.trim().toUpperCase();
  try {
    if (t && TYPED_INITIALS_RE.test(t)) window.localStorage.setItem(TYPED_INITIALS_KEY, t);
    else if (!t) window.localStorage.removeItem(TYPED_INITIALS_KEY);
  } catch {
    /* a browser that refuses storage still presses; the initials just are not remembered */
  }
}

export function initialsOfEmail(email: string): string {
  return String(email ?? '').trim().toLowerCase().split('@')[0]!.split('.').map((p) => p.replace(/[^a-z0-9]/g, '').charAt(0)).filter(Boolean).join('').toUpperCase();
}

/** A date and time as the record shows one ("9/19/2026 3:18 PM"), on the Pacific clock whatever the browser's zone. */
export function fmtDateTimePt(iso: string): string {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return iso || '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles', month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  }).format(d).replace(',', '');
}

/** Who marked, in the shortest true words: the initials of a Care operator, or the platform. */
export function markedByWords(by: string, source: 'care' | 'platform' = 'care'): string {
  if (source === 'platform' || by === 'platform') return LANE_WORDING.sourcePlatform;
  return initialsOfEmail(by) || by;
}

/** The mark's words on a row: "Opted out Sep 19, 3:18 PM by MH" / "STOP Sep 19, 3:18 PM by MH". */
export function markWords(m: Pick<RowMark, 'kind' | 'at' | 'by'>): string {
  const word = m.kind === MARK_STOP_KIND ? LANES.stop.markWord : LANES.optout.markWord;
  return `${word} ${fmtTsPt(m.at)} by ${markedByWords(m.by)}`;
}

/** The lines the Other comms panel prints beside the archived history: "Opt out (26a1ckedgen) 9/19/2026 3:18 PM by MH". */
export function markLines(marks: readonly CareMarkRow[]): string[] {
  return marks.map((m) => {
    const word = m.kind === MARK_STOP_KIND ? 'STOP' : `Opt out (${m.campaign ?? 'no campaign'})`;
    return `${word} ${fmtDateTimePt(m.markedAt)} by ${markedByWords(m.markedBy, m.source)}`;
  });
}

/** The scope words of the Opt Out tab's note, from the registry's program year for the campaigns on screen. */
export function scopeNote(
  rows: ReadonlyArray<Pick<EventRow, 'campaign'>>,
  scopes: Readonly<Record<string, { program: string; year: number }>> | undefined,
): string {
  const seen = new Map<string, { program: string; year: number } | null>();
  for (const r of rows) {
    const c = r.campaign ?? '';
    if (!seen.has(c)) seen.set(c, (c && scopes?.[c]) || null);
  }
  const named = [...seen.values()].filter((v): v is { program: string; year: number } => v !== null);
  if (named.length === 0) return LANE_WORDING.scopeCampaignOnly;
  const distinct = new Set(named.map((v) => `${v.program} ${v.year}`));
  if (named.length === seen.size && distinct.size === 1) return LANE_WORDING.scopeProgramYear(named[0]!.program, named[0]!.year);
  return LANE_WORDING.scopeMixed;
}
